import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useDropzone, FileRejection } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { FaSteam } from 'react-icons/fa';
import { FiArrowLeft, FiEye, FiEyeOff, FiUpload } from 'react-icons/fi';
import { useAuthStore } from '../../store/authStore';
import { apiFetch } from '../../lib/api';
import { mapBackendUserToApp } from '../../lib/authUser';
import { useLanguage } from '../../contexts/LanguageContext';
import { PageHeader } from '../../components/UI';
import Dialog from '../../components/UI/Dialog';
import { Action, Field, request, refreshUser, steamLogin } from '../MVP/shared';
import { monogram } from '../MVP/home/primitives';

const MAX_BYTES = 3 * 1024 * 1024;
const ACCEPT = { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] };

const Page = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 24px;
  @media (max-width: 767px) {
    gap: 16px;
  }
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 720px) 280px;
  gap: 24px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 720px);
    .preview {
      order: -1;
    }
  }
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Stack = styled.div`
  display: grid;
  gap: 24px;
  min-width: 0;
  @media (max-width: 767px) {
    gap: 16px;
  }
`;
const Surface = styled.section`
  min-width: 0;
  display: grid;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
`;
const Field2 = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
  label,
  .lbl {
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .err {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.danger};
  }
  .hint {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Upload = styled.div<{ $wide?: boolean; $active?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$wide ? '1fr' : 'auto minmax(0, 1fr)')};
  gap: 12px 16px;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  background: ${(p) => (p.$active ? p.theme.colors.primarySoft : p.theme.colors.input)};
  transition: background ${(p) => p.theme.motion.fast};
  .thumb {
    position: relative;
    display: grid;
    place-items: center;
    overflow: hidden;
    border-radius: 10px;
    background: ${(p) => p.theme.colors.navigation};
    color: ${(p) => p.theme.colors.textMuted};
    font-size: 24px;
    font-weight: 650;
    width: ${(p) => (p.$wide ? '100%' : '72px')};
    height: ${(p) => (p.$wide ? 'auto' : '72px')};
    aspect-ratio: ${(p) => (p.$wide ? '5 / 1' : '1')};
    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    span {
      font-size: 13px;
      font-weight: 500;
    }
  }
  .body {
    display: grid;
    gap: 6px;
    min-width: 0;
    justify-items: start;
  }
  .meta {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
    overflow-wrap: anywhere;
  }
`;
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  .state {
    margin-right: auto;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .err {
    flex-basis: 100%;
    font-size: 14px;
    color: ${(p) => p.theme.colors.danger};
  }
`;
const Rows = styled.dl`
  display: grid;
  gap: 0;
  > div {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px 16px;
    min-height: 60px;
    padding: 8px 0;
  }
  dt {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  dd {
    min-width: 0;
    font-size: 15px;
    overflow-wrap: anywhere;
  }
  .ok {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${(p) => p.theme.colors.success};
    font-weight: 600;
  }
  .muted {
    color: ${(p) => p.theme.colors.textMuted};
  }
  @media (max-width: 600px) {
    > div {
      grid-template-columns: minmax(0, 1fr) auto;
      dt {
        grid-column: 1 / -1;
      }
    }
  }
`;
const Toggle = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 4px 12px;
  align-items: start;
  min-height: 44px;
  cursor: pointer;
  input {
    width: 20px;
    height: 20px;
    margin-top: 2px;
  }
  strong {
    font-size: 15px;
    font-weight: 600;
  }
  small {
    grid-column: 2;
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Preview = styled.aside`
  position: sticky;
  top: 72px;
  overflow: hidden;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  .cover {
    height: 96px;
    background: ${(p) => p.theme.colors.navigation} center / cover no-repeat;
  }
  .body {
    display: grid;
    gap: 6px;
    padding: 0 20px 20px;
    margin-top: -36px;
  }
  .av {
    position: relative;
    display: grid;
    place-items: center;
    width: 72px;
    height: 72px;
    overflow: hidden;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.surfaceHover};
    box-shadow: 0 0 0 4px ${(p) => p.theme.colors.surface};
    font-size: 26px;
    font-weight: 650;
    color: ${(p) => p.theme.colors.textSecondary};
    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .name {
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .cap {
    font-size: 12px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  @media (max-width: 1100px) {
    position: static;
  }
`;

type Draft = { avatar?: string; background?: string };
type FileInfo = { name: string; size: number } | null;
const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / 1024))} КБ`);

/** Поле изображения: текущее значение или черновик, «Заменить», drag and drop, ошибка рядом. */
function ImageField({
  id,
  label,
  wide,
  current,
  draft,
  info,
  error,
  onFile,
  onError,
}: {
  id: string;
  label: string;
  wide?: boolean;
  current?: string;
  draft?: string;
  info: FileInfo;
  error: string;
  onFile: (file: File) => void;
  onError: (msg: string) => void;
}) {
  const { t } = useLanguage();
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_BYTES,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: (f) => f[0] && onFile(f[0]),
    onDropRejected: (r: FileRejection[]) => {
      const code = r[0]?.errors[0]?.code;
      onError(code === 'file-too-large' ? t('profile.err_size') : t('profile.err_type'));
    },
  });
  const src = draft || current;
  return (
    <Field2>
      <span className="lbl" id={id}>
        {label}
      </span>
      <Upload {...getRootProps()} $wide={wide} $active={isDragActive} aria-labelledby={id}>
        <input {...getInputProps()} aria-labelledby={id} />
        <div className="thumb">{src ? <img src={src} alt="" /> : wide ? <span>{t('profile.no_background')}</span> : null}</div>
        <div className="body">
          <Action type="button" onClick={open}>
            <FiUpload aria-hidden="true" />
            {src ? t('profile.replace') : t('profile.choose')}
          </Action>
          <span className="meta">{info ? `${info.name}, ${kb(info.size)}` : `${t('profile.file_hint')} · ${t('profile.drop_hint')}`}</span>
        </div>
      </Upload>
      {error && (
        <span className="err" role="alert">
          {error}
        </span>
      )}
    </Field2>
  );
}

/* ───────────────────────── смена email / пароля ───────────────────────── */
function CredentialDialog({ mode, onClose }: { mode: 'email' | 'password'; onClose: () => void }) {
  const { t } = useLanguage();
  const user = useAuthStore((s) => s.user);
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [show, setShow] = useState(false);
  const [sent, setSent] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ value?: string; code?: string; form?: string }>({});
  const target = mode === 'email' ? value.trim() : user?.email || '';

  const validValue = () => {
    if (mode === 'email' && !/^\S+@\S+\.\S+$/.test(value.trim())) return t('account.err_email');
    if (mode === 'password' && value.length < 1) return t('account.err_password');
    return '';
  };

  async function sendCode() {
    const err = validValue();
    if (err) return setErrors({ value: err });
    setBusy(true);
    setErrors({});
    try {
      await request('/auth/code', { email: target, purpose: mode });
      setSent(target);
    } catch (e) {
      setErrors({ form: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    const next: typeof errors = {};
    const v = validValue();
    if (v) next.value = v;
    if (!code.trim()) next.code = t('account.err_code');
    if (next.value || next.code) return setErrors(next);
    setBusy(true);
    setErrors({});
    try {
      await request('/auth/' + mode, mode === 'email' ? { email: value.trim(), code: code.trim() } : { password: value, code: code.trim() });
      await refreshUser();
      toast.success(t(mode === 'email' ? 'account.saved_email' : 'account.saved_password'));
      onClose();
    } catch (e) {
      setErrors({ form: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }
  const title = mode === 'email' ? t('account.change_email') : user?.hasPassword ? t('account.change_password') : t('account.set_password');

  return (
    <Dialog
      size="sm"
      label={title}
      onClose={onClose}
      dirty={!!value || !!code}
      footer={
        <>
          <Action type="button" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Action>
          <Action type="button" $variant="primary" onClick={confirm} disabled={busy} aria-busy={busy}>
            {t('account.confirm')}
          </Action>
        </>
      }
    >
      <h2>{title}</h2>
      <form
        style={{ display: 'grid', gap: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          confirm();
        }}
      >
        <Field2>
          <label htmlFor="cred-value">{mode === 'email' ? t('account.new_email') : t('account.new_password')}</label>
          <div style={{ position: 'relative' }}>
            <Field
              id="cred-value"
              type={mode === 'email' ? 'email' : show ? 'text' : 'password'}
              autoComplete={mode === 'email' ? 'email' : 'new-password'}
              placeholder={mode === 'email' ? 'you@example.com' : ''}
              value={value}
              aria-invalid={!!errors.value}
              aria-describedby={errors.value ? 'cred-value-err' : undefined}
              onChange={(e) => {
                setValue(e.target.value);
                if (errors.value) setErrors((x) => ({ ...x, value: undefined }));
              }}
              style={mode === 'password' ? { paddingRight: 48 } : undefined}
            />
            {mode === 'password' && (
              <button
                type="button"
                aria-label={show ? t('account.hide_password') : t('account.show_password')}
                aria-pressed={show}
                onClick={() => setShow((v) => !v)}
                style={{ position: 'absolute', right: 0, top: 0, width: 48, height: 48, display: 'grid', placeItems: 'center', color: 'inherit' }}
              >
                {show ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
              </button>
            )}
          </div>
          {errors.value && (
            <span className="err" id="cred-value-err" role="alert">
              {errors.value}
            </span>
          )}
          <span className="hint">{mode === 'email' ? t('account.sent_new') : t('account.sent_account')}</span>
        </Field2>

        <Field2>
          <label htmlFor="cred-code">{t('account.code')}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8 }}>
            <Field
              id="cred-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              aria-invalid={!!errors.code}
              aria-describedby={errors.code ? 'cred-code-err' : undefined}
              onChange={(e) => {
                setCode(e.target.value);
                if (errors.code) setErrors((x) => ({ ...x, code: undefined }));
              }}
            />
            <Action type="button" onClick={sendCode} disabled={busy}>
              {sent ? t('account.resend_code') : t('account.get_code')}
            </Action>
          </div>
          {errors.code && (
            <span className="err" id="cred-code-err" role="alert">
              {errors.code}
            </span>
          )}
          {sent && (
            <span className="hint" role="status">
              {t('account.code_sent', { email: sent })}
            </span>
          )}
        </Field2>
        {errors.form && (
          <p className="err" role="alert" style={{ color: 'inherit' }}>
            {errors.form}
          </p>
        )}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/* ───────────────────────── страница ───────────────────────── */
const ProfileSettings: React.FC = () => {
  const { t } = useLanguage();
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [name, setName] = useState(user?.username || '');
  const [draft, setDraft] = useState<Draft>({});
  const [info, setInfo] = useState<{ avatar: FileInfo; background: FileInfo }>({ avatar: null, background: null });
  const [errors, setErrors] = useState<{ name?: string; avatar?: string; background?: string; form?: string }>({});
  const [saving, setSaving] = useState(false);
  const [cred, setCred] = useState<'email' | 'password' | null>(null);
  const [tagBusy, setTagBusy] = useState(false);
  const saveLock = useRef(false);

  useEffect(() => {
    if (!user?.username) return;
    setName((n) => (n === '' ? user.username : n));
  }, [user?.username]);

  const nameChanged = name.trim() !== (user?.username || '');
  const dirty = nameChanged || !!draft.avatar || !!draft.background;
  useEffect(() => {
    if (!dirty) return;
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const read = (key: 'avatar' | 'background') => (file: File) => {
    setErrors((e) => ({ ...e, [key]: undefined, form: undefined }));
    const reader = new FileReader();
    reader.onload = () => {
      setDraft((d) => ({ ...d, [key]: reader.result as string }));
      setInfo((i) => ({ ...i, [key]: { name: file.name, size: file.size } }));
    };
    reader.onerror = () => setErrors((e) => ({ ...e, [key]: t('profile.err_read') }));
    reader.readAsDataURL(file);
  };

  function discard() {
    setName(user?.username || '');
    setDraft({});
    setInfo({ avatar: null, background: null });
    setErrors({});
  }

  async function save() {
    if (saveLock.current || !dirty) return;
    if (name.trim().length < 2) return setErrors((e) => ({ ...e, name: t('profile.username_short') }));
    saveLock.current = true;
    setSaving(true);
    setErrors({});
    const body: Record<string, string> = {};
    if (nameChanged) body.username = name.trim();
    if (draft.avatar) body.avatar = draft.avatar;
    if (draft.background) body.background = draft.background;
    try {
      const res = await apiFetch('/auth/me', { method: 'PATCH', body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || t('profile.save_failed'));
      updateUser(mapBackendUserToApp(data, user?.stats));
      setDraft({});
      setInfo({ avatar: null, background: null });
      toast.success(t('profile.saved'));
    } catch (e) {
      setErrors({ form: e instanceof Error ? e.message : t('profile.save_failed') });
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  }

  async function toggleTag(on: boolean) {
    setTagBusy(true);
    try {
      await request('/auth/me', { showClanTag: on }, 'PATCH');
      await refreshUser();
      toast.success(t('account.setting_saved'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setTagBusy(false);
    }
  }

  return (
    <Page>
      <Action as={Link} to="/profile" $variant="ghost" style={{ flex: "0 0 auto", justifySelf: "start", alignSelf: "flex-start", width: "fit-content" }}>
        <FiArrowLeft aria-hidden="true" />
        {t('profile.back')}
      </Action>
      <PageHeader title={t('profile.settings_title')} />
      <Layout>
        <Stack>
          <Surface as="form" aria-labelledby="appearance-title" onSubmit={(e: React.FormEvent) => { e.preventDefault(); save(); }}>
            <h2 id="appearance-title">{t('profile.appearance')}</h2>
            <Field2>
              <label htmlFor="profile-name">{t('profile.username_label')}</label>
              <Field
                id="profile-name"
                value={name}
                maxLength={30}
                autoComplete="nickname"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'profile-name-err' : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name && e.target.value.trim().length >= 2) setErrors((x) => ({ ...x, name: undefined }));
                }}
              />
              {errors.name && (
                <span className="err" id="profile-name-err" role="alert">
                  {errors.name}
                </span>
              )}
            </Field2>
            <ImageField id="avatar-lbl" label={t('profile.avatar_label')} current={user?.avatar} draft={draft.avatar} info={info.avatar} error={errors.avatar || ''} onFile={read('avatar')} onError={(m) => setErrors((e) => ({ ...e, avatar: m }))} />
            <ImageField id="bg-lbl" wide label={t('profile.background_label')} current={user?.background} draft={draft.background} info={info.background} error={errors.background || ''} onFile={read('background')} onError={(m) => setErrors((e) => ({ ...e, background: m }))} />
            <Bar>
              {dirty && <span className="state">{t('profile.unsaved')}</span>}
              <Action type="button" $variant="ghost" onClick={discard} disabled={!dirty || saving}>
                {t('profile.discard')}
              </Action>
              <Action type="submit" $variant="primary" disabled={!dirty || saving} aria-busy={saving}>
                {t('profile.save')}
              </Action>
              {errors.form && (
                <span className="err" role="alert">
                  {errors.form}
                </span>
              )}
            </Bar>
          </Surface>

          <Surface aria-labelledby="account-title">
            <h2 id="account-title">{t('profile.account')}</h2>
            <Rows>
              <div>
                <dt>{t('account.email')}</dt>
                <dd className={user?.email ? undefined : 'muted'}>{user?.email || t('account.email_none')}</dd>
                <Action type="button" onClick={() => setCred('email')}>
                  {t('account.change_email')}
                </Action>
              </div>
              <div>
                <dt>{t('auth.password')}</dt>
                <dd className="muted">{user?.hasPassword ? '••••••••' : t('profile.no_data')}</dd>
                <Action type="button" disabled={!user?.email} title={!user?.email ? t('account.email_none') : undefined} onClick={() => setCred('password')}>
                  {user?.hasPassword ? t('account.change_password') : t('account.set_password')}
                </Action>
              </div>
              <div>
                <dt>{t('account.steam')}</dt>
                {user?.steamId ? (
                  <dd>
                    <span className="ok">
                      <FaSteam aria-hidden="true" />
                      {t('account.steam_linked')}
                    </span>
                  </dd>
                ) : (
                  <>
                    <dd className="muted">{t('home.steam_not_linked')}</dd>
                    <Action type="button" onClick={() => steamLogin().catch((e) => toast.error(e.message))}>
                      <FaSteam aria-hidden="true" />
                      {t('account.steam_link')}
                    </Action>
                  </>
                )}
              </div>
            </Rows>
            <Toggle>
              <input type="checkbox" checked={user?.showClanTag !== false} disabled={tagBusy} onChange={(e) => toggleTag(e.target.checked)} />
              <strong>{t('account.clan_tag')}</strong>
              <small>{t('account.clan_tag_hint')}</small>
            </Toggle>
          </Surface>
        </Stack>

        <Preview className="preview" aria-label={t('profile.preview')}>
          <div className="cover" style={(draft.background || user?.background) ? { backgroundImage: `url(${JSON.stringify(draft.background || user?.background)})` } : undefined} />
          <div className="body">
            <div className="av" aria-hidden="true">
              {draft.avatar || user?.avatar ? <img src={draft.avatar || user?.avatar} alt="" /> : monogram(name)}
            </div>
            <div className="name">{name || user?.username}</div>
            <div className="cap">{t('profile.preview')}</div>
          </div>
        </Preview>
      </Layout>
      {cred && <CredentialDialog mode={cred} onClose={() => setCred(null)} />}
    </Page>
  );
};

export default ProfileSettings;
